/* eslint-disable @typescript-eslint/no-unused-vars */
import { QuestionIcon } from '@phosphor-icons/react';
import {
  Checkbox, Col, Row, Tooltip
} from 'antd';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Typography } from 'tfc-components';

import { IForm } from '.';

import Icon from 'components/atoms/Icon';
import Loading from 'components/atoms/Loading';
import { ServicePackageData } from 'services/servicePackages/type';
import mapModifiers from 'utils/functions';

interface PackageSelectorProps {
  mainPackages: ServicePackageData[];
  duration: 'monthly' | 'yearly';
  setDuration: (duration: 'monthly' | 'yearly') => void;
  selectedMain: ServicePackageData | null;
  selectedAddons: ServicePackageData[];
  setSelectedAddons: (data: ServicePackageData) => void;
  isMainPackageLoading: boolean;
  isAddonPackageLoading: boolean;
  method?: UseFormReturn<IForm>
  billingType: 'one-time' | 'subscription'
  currentPackage: {
    name: string;
    duration: number;
    price: number;
    id?: number;
  };
  currentBillingType: number
}

const PackageSelector: React.FC<PackageSelectorProps> = ({
  duration,
  setDuration,
  selectedMain,
  selectedAddons,
  mainPackages,
  isMainPackageLoading,
  isAddonPackageLoading,
  method,
  setSelectedAddons,
  billingType,
  currentPackage,
  currentBillingType
}) => {
  const addOnPackages = mainPackages.find(
    (pkg) => pkg.id === selectedMain?.id
  )?.servicePackageAddOnData || [];

  return (
    <div>
      <div className="p-checkout_durationTabs">
        <Typography.Text fontweight="600" extendClasses="p-checkout_titleProduct fs-20x24">Select a Package</Typography.Text>
        <Row className="p-checkout_durationTabs_container" gutter={[32, 16]}>
          <Col span={4}>
            <Row
              onClick={() => {
                setDuration('yearly');
                method?.setValue('addOnPackage', [], { shouldDirty: true, shouldValidate: true });
              }}
            >
              <Typography.Text
                extendClasses={mapModifiers('fs-16x24 p-checkout_durationTabs_button', duration === 'yearly' && 'active')}
                fontweight="600"
              >
                Yearly
              </Typography.Text>
            </Row>
            <Row
              onClick={() => {
                setDuration('monthly');
                method?.setValue('addOnPackage', [], { shouldDirty: true, shouldValidate: true });
              }}
            >
              <Typography.Text
                fontweight="600"
                extendClasses={mapModifiers('fs-16x24 p-checkout_durationTabs_button', duration === 'monthly' && 'active')}
              >
                Monthly
              </Typography.Text>
            </Row>
          </Col>
          <Col span={20}>
            <Row>
              <div className="p-checkout_mainPackage">
                <div className="p-checkout_mainPackage_title">
                  <Typography.Text fontweight="600" extendClasses="p-checkout_mainPackage_titleText fs-16x20">Main package</Typography.Text>
                  <div className="p-checkout_mainPackage_titleDivider" />
                </div>
                {isMainPackageLoading ? <div className="p-checkout_mainPackage"><Loading /></div> : (
                  <div className="p-checkout_grid">
                    {mainPackages.map((pkg) => (
                      <div
                        key={pkg.id}
                        className={`p-checkout_packageCard ${selectedMain?.id === pkg.id ? 'selected' : ''} ${billingType === 'subscription' && currentBillingType === 2 && currentPackage.id === pkg.id && 'p-changePlan_disable'}`}
                        onClick={() => {
                          if (billingType === 'subscription'
                            && currentBillingType === 2
                            && currentPackage.id === pkg.id) return;
                          method?.setValue('mainPackage', { ...pkg, unit: 1, quantity: 1 }, { shouldDirty: true, shouldValidate: true });
                          method?.setValue('addOnPackage', [], { shouldDirty: true, shouldValidate: true });
                        }}

                      >
                        <div className="p-checkout_packageCard_info">
                          <Typography.Text fontweight="600" extendClasses="p-checkout_packageCard_name fs-18x21">
                            {pkg.name}

                            {billingType === 'subscription' && currentBillingType === 2 && currentPackage.id === pkg.id && (
                              <Tooltip title="You’re currently using this plan">
                                <QuestionIcon size={20} style={{ marginLeft: 4 }} />
                              </Tooltip>
                            )}
                          </Typography.Text>
                          <div className="p-checkout_packageCard_price">
                            <Typography.Text fontweight="600" extendClasses="p-checkout_packageCard_amount fs-18x21">
                              {pkg.price.toLocaleString('vi')}
                              {' '}
                              VND
                            </Typography.Text>
                            <span className="p-checkout_packageCard_duration">
                              /
                              {duration === 'monthly' ? 'month' : 'year'}
                            </span>
                          </div>
                        </div>
                        {selectedMain?.id === pkg.id && (
                          <div className="p-checkout_packageCard_check">
                            <Icon iconName="checkWhite" size="24" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Row>

            <Row>
              <div className="p-checkout_addonPackage">
                <div className="p-checkout_mainPackage_title">
                  <Typography.Text fontweight="600" extendClasses="p-checkout_mainPackage_titleText fs-16x20">Add-on package</Typography.Text>
                  <div className="p-checkout_mainPackage_titleDivider" />
                </div>
                {isAddonPackageLoading ? <div className="p-checkout_mainPackage"><Loading /></div> : (
                  <div className="p-checkout_grid">
                    {addOnPackages.length > 0 ? addOnPackages.map((pkg) => (
                      <div
                        key={pkg.id}
                        className={`p-checkout_packageCard p-checkout_packageCard_addon${selectedAddons.some((a) => a.packageId === pkg.id)
                          ? 'Selected' : ''}`}
                        onClick={() => {
                          setSelectedAddons({
                            ...pkg, packageId: pkg.id, unit: 1, quantity: 1
                          });
                        }}
                      >
                        <div className="p-checkout_packageCard_addOnInfo">
                          <div>
                            <Typography.Text fontweight="600" extendClasses="p-checkout_packageCard_name fs-18x21">{pkg.name}</Typography.Text>
                            <div className="p-checkout_packageCard_price">
                              <Typography.Text fontweight="600" extendClasses="p-checkout_packageCard_amount fs-18x21">
                                {pkg.price.toLocaleString('vi')}
                                {' '}
                                VND
                              </Typography.Text>
                              <span className="p-checkout_packageCard_duration">
                                /
                                {duration === 'monthly' ? 'month' : 'year'}
                              </span>
                            </div>
                          </div>
                          <Checkbox
                            type="checkbox"
                            checked={selectedAddons.some((a) => a.packageId === pkg.id)}
                            className="p-monitorDashboard_filter_optionCheckbox"
                          />
                        </div>
                        {pkg.description && (
                          <div className="p-checkout_packageCard_hover">
                            <Typography.Text extendClasses="fs-14x18">
                              {pkg.description}
                            </Typography.Text>
                          </div>
                        )}
                      </div>
                    ))
                      : (
                        <div className="p-checkout_packageCard_empty">
                          <Typography.Text fontweight="600" extendClasses="p-checkout_packageCard_name">No Available Package</Typography.Text>
                        </div>
                      )}
                  </div>
                )}
              </div>
            </Row>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default PackageSelector;
